/**
 * Constants for the Ride to Rebuild charity campaign.
 */

/**
 * Earnings per adult mile (in cents).
 */
export const ADULT_MILE_EARNING_CENTS = 100;

/**
 * Earnings per minor ride (in cents).
 */
export const MINOR_RIDE_EARNING_CENTS = 1000;

/**
 * Maximum contribution cap (in cents).
 */
export const CONTRIBUTION_CAP_CENTS = 300000;

/**
 * Minimum contribution floor (in cents).
 */
export const CONTRIBUTION_FLOOR_CENTS = 100000;

/**
 * Session token expiration time (30 days in milliseconds).
 */
export const SESSION_EXPIRATION_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Magic link token expiration time (20 minutes in milliseconds).
 */
export const MAGIC_LINK_EXPIRATION_MS = 20 * 60 * 1000;

/**
 * Longest household display name a rename may set.
 *
 * Must match the cap in the service's UpdateHouseholdRequestSchema, which
 * refuses anything longer.
 */
export const MAX_HOUSEHOLD_NAME_LENGTH = 60;

/**
 * Maximum file size for screenshot upload (in bytes, 10MB).
 */
export const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Supported image MIME types for evidence upload.
 */
export const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * The participant-terms version this build displays.
 *
 * Sent with every enrolment and stamped onto the member server-side, so a later
 * revision can be told apart from what someone actually agreed to. Must match
 * PARTICIPANT_TERMS_VERSION in the service, which refuses anything else.
 *
 * The service compares it for exact equality and answers 409 TermsOutOfDate on
 * any other value, so a revision that lands on one side alone stops every new
 * enrolment until the other follows. That is not a theoretical risk: it is what
 * this value being left at 2026-09-14 did while the service moved to
 * 2026-09-17. Move the two together, and see the lockstep test next to this.
 */
export const CHARITY_TERMS_VERSION = "2026-09-17";

/**
 * Where the contribution goes, in the wording the Foster City Chamber Foundation
 * approved. Reproduced verbatim wherever the destination is stated.
 */
/**
 * The fund the contribution is paid into, for labelling a figure too short to
 * carry the full destination statement.
 */
export const CHARITY_FUND_NAME = "Spinnaker Cove Fire Relief Fund";

export const CHARITY_FUND_DESTINATION_STATEMENT =
  "100% of proceeds will go to the Spinnaker Cove Fire Relief Fund organized by the Foster City Chamber Foundation, a 501c3 charity organization.";

/**
 * Where questions about the money go.
 *
 * The Foundation answers for the contribution and the campaign itself; Muggle AI
 * answers for the site. Splitting them is not decoration — a participant asking
 * where their miles went should not wait behind a password-reset queue.
 */
export const CHARITY_FOUNDATION_EMAIL = "admin@linwangfamilyfoundation.org";

/**
 * Address participants are given for questions about the campaign or their account.
 */
export const CHARITY_CONTACT_EMAIL = "support@muggle-ai.com";

/**
 * Where to write about adding to the contribution once the cap is reached.
 *
 * The Foundation's own address rather than the campaign's support inbox: past
 * the cap the conversation is about giving money, which is the Foundation's to
 * have. Matches the address published in the campaign's participant rules.
 */
export const CHARITY_TOP_UP_CONTACT_EMAIL = CHARITY_FOUNDATION_EMAIL;

/**
 * API base path for charity endpoints.
 */
export const CHARITY_API_BASE_PATH = "/charity";

/**
 * Query-string key the join page reads a prefilled address from.
 *
 * The create-your-household email builds its link around this name, so changing
 * it breaks every invite already sitting in someone's inbox.
 */
export const CHARITY_JOIN_EMAIL_PARAM = "email";

/**
 * Query-string key carrying the invitation token on a join link.
 *
 * Must stay in step with the backend constant of the same name — renaming it on
 * one side alone quietly turns every invitation already sent into a cold join,
 * which still works but asks for the second mail this exists to avoid.
 */
export const CHARITY_INVITE_TOKEN_PARAM = "invite";

/**
 * API base path for charity admin endpoints (Auth0-gated).
 */
export const CHARITY_ADMIN_API_BASE_PATH = "/charity/admin";

/**
 * Earnings per adult mile (in cents).
 */
export const ADMIN_ADULT_MILE_EARNING_CENTS = 100;

/**
 * Earnings per minor ride (in cents).
 */
export const ADMIN_MINOR_RIDE_EARNING_CENTS = 1000;

/**
 * Session storage keys for preserving report flow state.
 */
export const SESSION_STORAGE_KEYS = {
  reportSelectedRiders: "charity_report_selected_riders",
  reportEvidenceJobId: "charity_report_evidence_job_id",
  reportMiles: "charity_report_miles",
  reportRideDate: "charity_report_ride_date",
  reportNote: "charity_report_note",
} as const;

/**
 * Local storage keys for persisting user preferences.
 */
export const LOCAL_STORAGE_KEYS = {
  sessionToken: "charity_session_token",
  householdId: "charity_household_id",
  lastUsedMiles: "charity_last_used_miles",
  // Kept so reloading the window that asked for a link resumes its wait rather
  // than dropping back to an empty form with a link already in the inbox.
  pendingSignInId: "charity_pending_sign_in_id",
  pendingSignInToken: "charity_pending_sign_in_token",
  // The address that wait belongs to, so a window returning to one can show it
  // back and let a mistyped address be corrected rather than only waited out.
  pendingSignInEmail: "charity_pending_sign_in_email",
  // When another mail may go. Kept here rather than in page state because the
  // server's clock keeps running while a participant is away, and a notice that
  // resets on every mount tells them nothing about the wait they are actually in.
  pendingSignInNextAllowedAtMs: "charity_pending_sign_in_next_allowed_at_ms",
} as const;
